// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const EvidenceCategory = {
    IDENTITY_PROOF: 'identity_proof',
    CORRESPONDENCE: 'correspondence',
    INTERNAL_DATA: 'internal_data',
    LEGAL: 'legal',
    OTHER: 'other',
};
export const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
    'message/rfc822',
    // Sprint 43.5.2 — platform-generated export formats. These are produced
    // by UnifiedExportService (ZipBuilder for evidence packs / per-case
    // bundles; DOCX/XLSX for reports; JSON for machine-readable outputs).
    // Without these on the global allow-list, the org-policy MIME check in
    // GenericAttachmentService.upload() rejects the server-generated bytes
    // with "File type 'application/zip' is not allowed by the org policy".
    'application/zip',
    'application/x-zip-compressed',
    'application/json',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
export const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_TOTAL_ATTACHMENTS = 50;
export const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500 MB
// ────────────────────────────────────────────────────────────────
// Generic attachments (Sprint 41.0.0)
//
// A single shared attachment surface that any module can use without
// coupling to Rights Requests. Backed by `grc_attachments` collection
// and mounted at `/api/v1/attachments`.
// ────────────────────────────────────────────────────────────────
/**
 * Allowlist of owner types that can receive attachments.
 *
 * **Contract:** every value listed here MUST also be registered in
 * `OWNER_TYPE_ALLOWLIST` in `GenericAttachmentService` (backend). A drift
 * test enforces the two lists stay in sync.
 */
export const AttachmentOwnerType = {
    // Sprint 41.0.0 — Governance (G2-G5)
    GOVERNANCE_EVIDENCE: 'governance_evidence',
    PLAYBOOK_EXECUTION_STEP: 'playbook_execution_step',
    OFFBOARDING_DATA_RETURN: 'offboarding_data_return',
    APPROVAL_DECISION: 'approval_decision',
    // Sprint 41.1.0 — Vendor Risk (V2-V3)
    VENDOR_QUESTIONNAIRE_ANSWER: 'vendor_questionnaire_answer',
    VENDOR_ONBOARDING_CASE: 'vendor_onboarding_case',
    // Sprint 41.2.0 E1 — Rights Requests migration off legacy AttachmentService
    RIGHTS_REQUEST_ATTACHMENT: 'rights_request_attachment',
    // Sprint 41.3.0 F0 — Platform-wide AttachmentField adoption
    DATA_MAPPING_EVIDENCE: 'data_mapping_evidence',
    DELETION_STEP_EXECUTION: 'deletion_step_execution',
    INCIDENT_EVIDENCE: 'incident_evidence',
    MONITORING_TASK_INSTANCE: 'monitoring_task_instance',
    DPIA_RECORD: 'dpia_record',
    TRANSFER_ASSESSMENT_RECORD: 'transfer_assessment_record',
    FORM_INSTANCE_RESPONSE: 'form_instance_response',
    CONSENT_WORKFLOW: 'consent_workflow',
    INCIDENT_OPS_EVIDENCE: 'incident_ops_evidence',
    // Sprint 41.4.0 F0.4 — Standalone export persistence (organization-
    // owned, no owner_id). Consumed by Sprint 41.5.0 (Governance final
    // polish: cross-border filing PDFs, board reports, evidence bundles)
    // and Sprint 41.6.0 (Vendor Risk final polish: case evidence packs,
    // incident report PDFs, vendor onboarding bundles). These rows have
    // `owner_id = null` because the owning concept is the org itself,
    // not a single entity in another collection.
    GOVERNANCE_CROSS_BORDER_EXPORT: 'governance_cross_border_export',
    GOVERNANCE_BOARD_REPORT_EXPORT: 'governance_board_report_export',
    GOVERNANCE_EVIDENCE_BUNDLE_EXPORT: 'governance_evidence_bundle_export',
    CASE_EVIDENCE_PACK_EXPORT: 'case_evidence_pack_export',
    // Sprint 58.0.0 (Phase 25 / Sprint A) — PDPL DPIA / OTIA evidence pack
    // ZIPs produced by PdplEvidencePackZipService and routed through
    // UnifiedExportService per R-33. Two variants so storage / retention /
    // metadata can diverge if the regulator differentiates the form types.
    // Sprint 62.2.2 — cross-framework evidence pack exports (ZIP + the
    // machine-readable JSON companion). Org-owned with no single owner_id: the
    // pack spans every framework in a resolved graph, so no one record owns it.
    // Deliberately distinct from the two PDPL owner types below, which serve one
    // regulator-facing artefact whose retention and metadata may diverge.
    FRAMEWORK_EVIDENCE_PACK_EXPORT: 'framework_evidence_pack_export',
    /**
     * Sprint 62.2.5 — proof of a **hard-copy** dispatch: the posting receipt,
     * courier slip or signed acknowledgement an operator uploads after sending a
     * pack on paper.
     *
     * Distinct from the export type above, and the distinction is not cosmetic.
     * The export is machine-produced, org-owned and hash-bound to a version; this
     * is human-supplied evidence about a physical event, owned by the submission
     * record. Collapsing them would make "the archive we generated" and "the
     * receipt the post office gave us" indistinguishable in a filed pack.
     *
     * 62.2.4 required `hard_copy.proof` and shipped no owner type for it, so
     * nothing could actually upload one — the field was mandatory and
     * unsatisfiable through the generic attachment route.
     */
    FRAMEWORK_EVIDENCE_PACK_HARD_COPY_PROOF: 'framework_evidence_pack_hard_copy_proof',
    PDPL_EVIDENCE_PACK_DPIA_EXPORT: 'pdpl_evidence_pack_dpia_export',
    PDPL_EVIDENCE_PACK_OTIA_EXPORT: 'pdpl_evidence_pack_otia_export',
    INCIDENT_REPORT_EXPORT: 'incident_report_export',
    VENDOR_ONBOARDING_EXPORT: 'vendor_onboarding_export',
    // Sprint 47.5.3b — Forseti AI-generated artifacts (DOCX outputs from
    // `generate_docx`, redlined DOCX from accept_edit). Org-owned, no
    // single owner_id (the AI surface produces these without a parent
    // record). Consumed by the chat surface in 47.5.4 to surface
    // "View / Download" on the message that triggered the generation.
    FORSETI_ARTIFACT: 'forseti_artifact',
    // Sprint 42.1.0 E4 — Data Mapping PDF exports now persist through
    // the File Component infrastructure instead of raw buffer return.
    DATA_MAPPING_EXPORT: 'data_mapping_export',
    // Sprint 42.1.0 E5 — Classification PDF exports
    CLASSIFICATION_EXPORT: 'classification_export',
    // Sprint 62.13.0 — penalty-exposure report and enforcement-case register
    // exports through UnifiedExportService (R-33). Each embeds the installed
    // pack code/version its figures came from.
    PENALTY_EXPOSURE_EXPORT: 'penalty_exposure_export',
    ENFORCEMENT_CASE_EXPORT: 'enforcement_case_export',
    // Sprint 62.14.0 — direction compliance-trail, hold-register and
    // regulated-account register exports through UnifiedExportService (R-33).
    AUTHORITY_DIRECTION_EXPORT: 'authority_direction_export',
    LEGAL_HOLD_EXPORT: 'legal_hold_export',
    REGULATED_ACCOUNT_EXPORT: 'regulated_account_export',
    // Sprint 62.18.0 — a subject's access-history and sharing-history exports
    // through UnifiedExportService (R-33). One KINDED owner type over two
    // registers (the 64.92.0 contract): the retrieval grant resolves from
    // `(owner type, kind)` via `ISSUED_RECORD_EXPORT_GATES`.
    ISSUED_RECORD_EXPORT: 'issued_record_export',
    // Sprint 63.30.0 (#584) — the file a scheduled export run produced. KINDED by
    // the schedule's export type (the 64.92.0 contract): the retrieval grant
    // resolves from `(owner type, kind)` via `SCHEDULED_EXPORT_GATES`, so an audit
    // trail export downloads under the audit export grant, never under the
    // schedule read alone. `owner_id` is the run's `run_id`.
    SCHEDULED_EXPORT_RUN: 'scheduled_export_run',
    // Sprint 62.24.0 — one supply row and one provider record exported through
    // UnifiedExportService (R-33); each downloads under its register's `:read`.
    CREDIT_INFORMATION_SUPPLY_EXPORT: 'credit_information_supply_export',
    DATA_SERVICE_PROVIDER_EXPORT: 'data_service_provider_export',
    // Sprint 62.45.0 — the reporting-period and inspection evidence packs of the
    // Supervisory Reporting register, through UnifiedExportService (R-33); both
    // download under the register's `payments_evidence:read`.
    SUPERVISORY_REPORTING_EXPORT: 'supervisory_reporting_export',
    /**
     * Sprint 62.45.0 Slice H (AC23) — a file a Supervisory Reporting record
     * rests on: a check's signed report and receipt, a notice email, a request
     * response, a letter recommendation's report, an inspection's decision or
     * minutes.
     *
     * Owned by the RECORD it proves (`owner_id` is the record's `_id`), which
     * always exists before its evidence: files are bound by PATCH only, a create
     * that carries ids is refused, and the server refuses an id held under
     * another owner type or another record (`SUPERVISORY_ATTACHMENT_NOT_OWNED`) —
     * the 62.28.0 bind-on-PATCH pattern. One owner type across the register's
     * six record collections, because they share one read grant; the dependent
     * rows each collection holds are declared in `ATTACHMENT_EVIDENCE_DEPENDENTS`,
     * so a file a record names cannot be deleted.
     *
     * Registered in ALL THREE places a new owner type must appear: this union,
     * `OWNER_TYPE_DOWNLOAD_POLICY` (`payments_evidence:read`) and
     * `GenericAttachmentService`'s `OWNER_TYPE_ALLOWLIST`.
     */
    SUPERVISORY_REPORTING_EVIDENCE: 'supervisory_reporting_evidence',
    /**
     * Sprint 63.24.0 Slice B — a file a controlled-testing personnel record's role
     * rests on: the qualification evidence for one dated role. Owned by the RECORD
     * (`owner_id` is the record's id). Files are uploaded against the record and
     * bound to a role by PATCH, which refuses a file held under another owner type
     * or another record (`PERSONNEL_EVIDENCE_NOT_OWNED`). Download and upload both
     * follow `controlled_testing_case` (the case's read and manage grants).
     */
    CONTROLLED_TESTING_PERSONNEL_EVIDENCE: 'controlled_testing_personnel_evidence',
    /**
     * Sprint 63.24.0 Slice C — the technical statement a controlled-testing solution
     * record rests on. Owned by the SOLUTION record (`owner_id` is its id). Uploaded
     * against the record and bound by PATCH, which refuses a file held under another
     * owner type or record (`SOLUTION_EVIDENCE_NOT_OWNED`). Download and upload follow
     * `controlled_testing_case` (the case's read and manage grants).
     */
    CONTROLLED_TESTING_SOLUTION_EVIDENCE: 'controlled_testing_solution_evidence',
    CONTROLLED_TESTING_CHILD_RESULTS_REPORT: 'controlled_testing_child_results_report',
    /**
     * Sprint 63.24.0 Slice D — the supplement file a failed registry retrieval rests on. Owned by
     * the REGISTRY ROW (`owner_id` is its id). Uploaded against the row and bound by PATCH, which
     * refuses a file held under another owner type or row (`REGISTRY_EVIDENCE_NOT_OWNED`).
     */
    CONTROLLED_TESTING_REGISTRY_EVIDENCE: 'controlled_testing_registry_evidence',
    /**
     * Sprint 63.24.0 Slice F — the files a stop rests on: the application, the results report, the
     * stop plan, the authority's decision and its evidence, the stop notices and the liquidation
     * record. Owned by the CASE (`owner_id` is its id). Uploaded against the case and bound by the
     * stop, revocation and closure writes, each refusing a file not held under this case.
     */
    CONTROLLED_TESTING_STOP_EVIDENCE: 'controlled_testing_stop_evidence',
    /**
     * Sprint 63.24.0 Slice F — the files a completion rests on: the evaluation report, the results
     * report and the authority's copy of the certificate. Owned by the CASE (`owner_id` is its id).
     */
    CONTROLLED_TESTING_COMPLETION_EVIDENCE: 'controlled_testing_completion_evidence',
    /**
     * Sprint 62.50.0 — a file a supervision-information record rests on: the
     * photograph of a notice at head office, a copy of the information provided
     * to the branch, the readiness evidence. Owned by the RECORD it proves
     * (`owner_id` is the record's `_id`), uploaded against the record after it
     * exists and bound by its PATCH/PUT `evidence_attachment_ids`, which refuses a
     * file held under another owner type or another record
     * (`PCF_ATTACHMENT_NOT_OWNED`). One owner type across the register's record
     * collections, because they share one read grant; the dependent rows each
     * collection holds are declared in `ATTACHMENT_EVIDENCE_DEPENDENTS`, so a file
     * a record names cannot be deleted.
     *
     * Registered in ALL THREE places a new owner type must appear: this union,
     * `OWNER_TYPE_DOWNLOAD_POLICY` (`pcf_supervision:read`) and
     * `GenericAttachmentService`'s `OWNER_TYPE_ALLOWLIST`.
     */
    PCF_SUPERVISION_EVIDENCE: 'pcf_supervision_evidence',
    /**
     * Sprint 62.55.0 — a file a customs tax-administration record rests on (a
     * dossier's or received document's evidence, a payment receipt). Uploaded
     * against the record, then bound server-side by the record's PATCH
     * (`assertAttachmentBoundTo`); downloads under
     * `customs_tax_administration:read`; a file a record names cannot be deleted
     * (`ATTACHMENT_EVIDENCE_DEPENDENTS`).
     */
    CUSTOMS_TAX_ADMINISTRATION_EVIDENCE: 'customs_tax_administration_evidence',
    /**
     * Sprint 62.55.0 (AC-F2) — a customs tax-administration export produced through
     * `UnifiedExportService` (R-33). An org-wide register export; no single record
     * owns it. Downloads under `customs_tax_administration:read`.
     */
    CUSTOMS_TAX_ADMINISTRATION_EXPORT: 'customs_tax_administration_export',
    /**
     * Sprint 62.50.1 — one supervision-information register exported through
     * `UnifiedExportService` (R-33). Org-owned with no single `owner_id`: an
     * export spans every record of its register. It downloads under
     * `pcf_supervision:read`, the read that opens every register it reproduces.
     *
     * Registered in ALL THREE places a new owner type must appear: this union,
     * `OWNER_TYPE_DOWNLOAD_POLICY` and `GenericAttachmentService`'s
     * `OWNER_TYPE_ALLOWLIST` (with `null` in its owner-collection map and `NONE`
     * in `ATTACHMENT_EVIDENCE_DEPENDENTS`, as every register export is).
     */
    PCF_SUPERVISION_EXPORT: 'pcf_supervision_export',
    /**
     * Sprint 62.51.0 — a file a data-exploitation record rests on: the request as
     * sent, the authority's decision, a certificate's authority copy, a notice
     * copy, the evidence of a return, deletion or destruction. Owned by the RECORD
     * it proves (`owner_id` is the record's `_id`), uploaded against the record
     * after it exists and bound by the record's PATCH `evidence_attachment_ids`
     * (or a sub-record's own list), which refuses a file held under another owner
     * type or another record (`DE_ATTACHMENT_NOT_OWNED`). One owner type across
     * the seven record collections, because they share one read and one write
     * grant; the dependent rows each collection holds are declared in
     * `ATTACHMENT_EVIDENCE_DEPENDENTS`, so a file a record names cannot be deleted.
     *
     * Registered in ALL THREE places a new owner type must appear: this union,
     * `OWNER_TYPE_DOWNLOAD_POLICY` (`data_exploitation:read`) and
     * `GenericAttachmentService`'s `OWNER_TYPE_ALLOWLIST`.
     */
    DATA_EXPLOITATION_EVIDENCE: 'data_exploitation_evidence',
    /**
     * Sprint 62.51.0 — a data-exploitation register, or one grant with every
     * record resting on it, exported through `UnifiedExportService` (R-33).
     * Org-owned with no single `owner_id`. Downloads under
     * `data_exploitation:read`, the read that opens every register it reproduces.
     */
    DATA_EXPLOITATION_EXPORT: 'data_exploitation_export',
    /**
     * Sprint 62.52.0 — a file a cybersecurity assurance index record rests on:
     * the evidence of the period behind an item's Mức or exclusion, a
     * statistic's source, or the evidence a supplier returned. Owned by the
     * PERIOD (`owner_id` is the period's `_id`), uploaded and downloaded only
     * through the module's membership-checked routes
     * (`POST|GET /api/v1/cybersecurity-assurance-index/periods/:periodId/evidence…`,
     * owner answer OQ-2), and named by an entry, a statistic or a supplier
     * request in its `evidence` / `response_evidence` links. Never accepted on a
     * record resting on a restricted-evidence requirement (D2).
     *
     * Registered in every place a new owner type must appear: this union,
     * `OWNER_TYPE_DOWNLOAD_POLICY` (`cybersecurity_assurance_index:restricted_read`
     * — the generic route serves it only to a holder of the restricted read),
     * `GenericAttachmentService`'s `OWNER_TYPE_ALLOWLIST`,
     * `ATTACHMENT_EVIDENCE_DEPENDENTS` and `owner-record-write-grant.ts`.
     */
    CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE: 'cybersecurity_assurance_index_evidence',
    /**
     * Sprint 62.52.0 — one period exported through `UnifiedExportService`
     * (R-33), its first page labelled as the organisation's own self-assessment
     * (AC15). Owned by the period; downloaded through the module's
     * membership-checked route, and on the generic route only under
     * `cybersecurity_assurance_index:restricted_read`.
     */
    CYBERSECURITY_ASSURANCE_INDEX_EXPORT: 'cybersecurity_assurance_index_export',
    /**
     * Sprint 62.49.0 — a file a food traceability record rests on: a registration
     * document, an internal control procedure, a certificate, a laboratory report,
     * an inspection finding or response, a warning's source, a notice's proof of
     * sending, a violation decision, a supplier's correction, a connection
     * receipt. Owned by the RECORD it proves (`owner_id` is the record's `_id`),
     * uploaded against the record after it exists and bound by the record's
     * `evidence_attachment_ids` (or a sub-record's own list), which refuses a
     * file held under another owner type or another record
     * (`FOOD_TRACEABILITY_ATTACHMENT_NOT_OWNED`). One owner type across the food
     * record collections, because they share one read and one write grant.
     *
     * Registered in every place a new owner type must appear (work file E0 row
     * 37): this union, `OWNER_TYPE_DOWNLOAD_POLICY` (`food_traceability:read`;
     * the upload grant `food_traceability:write` follows from it by verb
     * substitution, so no override row is needed), `GenericAttachmentService`'s `OWNER_TYPE_ALLOWLIST` and
     * `OWNER_TYPE_COLLECTIONS`, `ATTACHMENT_EVIDENCE_DEPENDENTS` and
     * `owner-record-write-grant.ts`.
     */
    FOOD_TRACEABILITY_EVIDENCE: 'food_traceability_evidence',
    /**
     * Sprint 62.49.0 — a food traceability register exported through
     * `UnifiedExportService` (R-33), or a recall case's internal copy of the
     * pack's report layout (AC14), labelled internal on its first page and
     * carrying no official form id. Produced by the platform only, never
     * uploaded. Downloads under `food_traceability:read`, the read that opens
     * every register it reproduces.
     */
    FOOD_TRACEABILITY_EXPORT: 'food_traceability_export',
    /**
     * Sprint 65.1.0 — the DPO work-programme and completeness package produced by
     * `ComplianceOperationsExportService` through `UnifiedExportService` (R-33).
     *
     * Org-owned with no single `owner_id`: the package spans every obligation the
     * organisation's installed content raised, so no one record owns it. Its
     * retrieval grant is `compliance_obligation:read` — the read that gates the
     * work programme itself — because the artefact carries the same rows the
     * programme shows.
     *
     * Registered in ALL THREE places a new owner type must appear: this union,
     * `OWNER_TYPE_DOWNLOAD_POLICY` and `GenericAttachmentService`'s
     * `OWNER_TYPE_ALLOWLIST`. Missing the third is a route that accepts an
     * `owner_type` the service then rejects — a silent 422 with no reader.
     */
    COMPLIANCE_OBLIGATION_EXPORT: 'compliance_obligation_export',
    /**
     * Sprint 63.25.0 (spec F3) — the optional proof that a version of an audit
     * evidence pack was sent. `owner_id` is the VERSION row's `_id`
     * (`grc_audit_evidence_pack_versions`), so the proof binds to the exact
     * dossier it accompanied; recording the submission refuses a file held under
     * any other version. The proof shows the pack was sent, not that the
     * recipient received or accepted it.
     */
    AUDIT_EVIDENCE_PACK_SUBMISSION: 'audit_evidence_pack_submission',
    // Sprint 42.2.2 — migrate remaining jsPDF usages to File Component
    INCIDENT_REPORTS_LIST_EXPORT: 'incident_reports_list_export',
    RIGHTS_REQUEST_RESPONSE_EXPORT: 'rights_request_response_export',
    // Sprint 42.2.3 E2 — unified CSV exports through UnifiedExportService
    AUDIT_EVENTS_EXPORT: 'audit_events_export',
    CONSENT_AUDIT_EXPORT: 'consent_audit_export',
    DISCOVERY_REPORT_EXPORT: 'discovery_report_export',
    FORM_FILING_CASE_LINKS_EXPORT: 'form_filing_case_links_export',
    ORG_GROUP_AUDIT_EXPORT: 'org_group_audit_export',
    // Sprint 42.2.3 E3 — access accountability client-side CSVs migrated
    ACCESS_ACCOUNTABILITY_EXPORT: 'access_accountability_export',
    // Consent evidence pack JSON exports through UnifiedExportService
    CONSENT_EVIDENCE_PACK_EXPORT: 'consent_evidence_pack_export',
    /**
     * Sprint 62.28.0 (D13, on the owner's ruling for idea-catalog #304) — the copy
     * of a marketing-communications consent the organisation holds ITSELF.
     *
     * Owned by the consent entry, not by a workflow. Sprint 62.24.0's consent
     * evidence page uploads its artefact under `CONSENT_WORKFLOW`, because at
     * create time the entry does not exist yet and an ownerless upload has to go
     * somewhere. The cost is that the file is bound to a workflow rather than to
     * the record that rests on it, so nothing can answer "is this attachment
     * actually this entry's?" — which is what #304 asked. Here the binding is the
     * entry: evidence is recorded by PATCH once the entry exists, and the server
     * refuses an attachment whose owner is not the entry being patched.
     */
    MARKETING_CONSENT_EVIDENCE: 'marketing_consent_evidence',
    /**
     * Sprint 62.29.0 — a file supporting one governance evidence entry (ethics
     * review, conformity status, model evaluation, AI-security testing, sector
     * criteria) on an AI system. Owned by the AI SYSTEM PROFILE, which exists
     * before any evidence is recorded, so the server binds every attachment to
     * the profile it is recorded against and refuses one owned elsewhere.
     */
    AI_GOVERNANCE_EVIDENCE: 'ai_governance_evidence',
    /**
     * Sprint 64 (E12.5 security S1) — the payment-authority export of one AI
     * system. Its own owner type, because it downloads under AUTHORITY, while
     * `ai_governance_evidence` downloads under READ: stored under the evidence
     * type, a READ-only holder with the file id could retrieve the authority
     * terms. Owned by the AI system profile, as the evidence type is.
     */
    AI_GOVERNANCE_AUTHORITY_EXPORT: 'ai_governance_authority_export',
    /**
     * Sprint 62.28.0 — proof that the confirmation owed back to a recipient was
     * actually sent. Owned by the SUPPRESSION ENTRY, which exists by the time the
     * confirmation is recorded, so this one is bindable where the consent
     * artefact is not: recording a confirmation is always a write against an
     * entry that is already there.
     */
    MARKETING_SUPPRESSION_EVIDENCE: 'marketing_suppression_evidence',
    /**
     * Sprint 62.46.0 — a file supporting one suspicious-activity case. Owned by
     * the CASE, which exists before evidence is attached, so the server binds
     * every file to the case it is recorded against.
     *
     * Retrieval is gated on `aml_case:read` — a custom-only grant — because a
     * case's existence must not reach a reader nobody named (tipping-off). A
     * file whose download policy were the broader payments read would tell its
     * holder that a case exists. Evidence held in the organisation's own systems
     * is referenced by id on the case (`evidence_refs`) instead of uploaded.
     */
    AML_CASE_EVIDENCE: 'aml_case_evidence',
    /**
     * Sprint 62.54.0 — the receipt for one fee line on a licence, certificate or
     * appraisal application (Sector Licences). Owned by the FEE LINE, which
     * exists before its receipt is uploaded, so the server binds the file to
     * the line it is recorded against and refuses one owned by another line or
     * organisation (AC10). The platform validates nothing about the receipt.
     */
    LICENCE_FEE_RECEIPT: 'licence_fee_receipt',
    /**
     * Sprint 62.54.0 — the receipt for one charge line on a sender-name
     * certificate (Marketing Communications, sender-identity side). Owned by the
     * charge line. Its own owner type, so retrieval follows the register that
     * holds the line and nothing wider.
     */
    SENDER_NAME_FEE_RECEIPT: 'sender_name_fee_receipt',
    /**
     * Sprint 62.54.0 — the authority's decision document on a licence,
     * certificate or appraisal application. Owned by the APPLICATION and bound
     * by the decision route only (AC6): no fee-line write reaches it.
     */
    LICENCE_FEE_DECISION_DOCUMENT: 'licence_fee_decision_document',
    /**
     * Sprint 62.56.0 — a file an evidence area of a biometric capture component
     * rests on, or a custody transfer's evidence. Owned by the RECORD it proves
     * (`owner_id` is the component's or the transfer's `_id`), uploaded after the
     * record exists and bound by the register's evidence-area PUT or custody
     * PATCH, which refuses a file held under another owner type or another record
     * (`BIOMETRIC_ATTACHMENT_NOT_OWNED`). One owner type across the two record
     * collections, because they share one read grant; each collection's dependent
     * rows are declared in `ATTACHMENT_EVIDENCE_DEPENDENTS`, so a file a record
     * names cannot be deleted. The register holds references, never a biometric
     * sample.
     */
    BIOMETRIC_CAPTURE_EVIDENCE: 'biometric_capture_evidence',
    /**
     * Sprint 62.56.0 — the inspection evidence pack produced through
     * `UnifiedExportService` (R-33). Org-owned: the pack spans the components,
     * determinations, links and custody records in the scope a person chose.
     */
    BIOMETRIC_CAPTURE_EXPORT: 'biometric_capture_export',
    /**
     * Sprint 62.55.0 (UC-VN-109 F-1, idea-catalog #684) — fee lines and the
     * decisions kept apart from them, exported through `UnifiedExportService`
     * (R-33) for one application, one sender-name certificate or one paid-on
     * month. Written only by `FeeLineExportService`; org-owned with no single
     * `owner_id` (a period export spans many records). Downloads under
     * `regulatory_fee_line:read`, the read that produces it. Registered in the
     * union, `OWNER_TYPE_DOWNLOAD_POLICY`, the attachment service's allowlist and
     * owner-collection map, `ATTACHMENT_EVIDENCE_DEPENDENTS` and the
     * system-generated set, as every register export is.
     */
    REGULATORY_FEE_LINE_EXPORT: 'regulatory_fee_line_export',
    // Sprint 42.2.3 E5b — generic CSV pass-through for frontend pages
    // that build CSV in-browser (consent, monitoring, etc.). Lets every
    // page surface its export in FilesBrowserPage without per-module
    // backend wiring.
    CLIENT_CSV_EXPORT: 'client_csv_export',
    // Sprint 41.7.0 R6 — Form output per-category owner types (regulator
    // submissions via FormOutputService). Distinct from the GOVERNANCE_*
    // and INCIDENT_REPORT_EXPORT types which are internal reports/summaries.
    FORM_OUTPUT_DPIA: 'form_output_dpia',
    FORM_OUTPUT_TRANSFER_ASSESSMENT: 'form_output_transfer_assessment',
    FORM_OUTPUT_DOSSIER: 'form_output_dossier',
    FORM_OUTPUT_INCIDENT_REPORT: 'form_output_incident_report',
    FORM_OUTPUT_BREACH_NOTIFICATION: 'form_output_breach_notification',
    FORM_OUTPUT_AUTHORITY_REQUEST: 'form_output_authority_request',
    FORM_OUTPUT_CROSS_BORDER_FILING: 'form_output_cross_border_filing',
    FORM_OUTPUT_OTHER: 'form_output_other',
    /**
     * Sprint 64.147.0 — the rendered output of an instance whose TEMPLATE carries
     * `access_tier: 'restricted'`.
     *
     * A separate owner type because eight of the nine above resolve to
     * `forms:instance:read` alone (`form_output_dossier` also requires `dpia:read`), and that is the wrong grant for a document that
     * reproduces the sections, labels and legal references of a state-secret
     * register. Measured: `auditor` can generate one (it holds
     * `forms:instance:export`) and `support` and `viewer` can then retrieve the
     * bytes (they hold `attachments:download` + `forms:instance:read`) — three
     * roles, none of which holds `regulatory_artifact:custody`.
     *
     * Fencing generation alone would not have closed that: a custodian who
     * legitimately generates one would still have produced a file every viewer
     * could download. The tier has to reach the artefact, not only the act.
     */
    FORM_OUTPUT_RESTRICTED_REGISTER: 'form_output_restricted_register',
    /**
     * Sprint 63.5.0 (idea-catalog #227) — a supporting document an operator
     * attaches to a SUBMISSION PACKAGE: the dossier they file can carry the
     * documents it references.
     *
     * Owned by the package, whose `_id` is the `owner_id`, so the package's
     * attachments are `grc_attachments` rows and not a second id array on the
     * package document. That is the ruling Sprint 64.71.0 took for
     * `data_registry_item_ids` and the reason `SubmissionPackage.document_attachments`
     * is a DERIVED field on the detail read rather than stored state: two
     * representations of one list is one list that can disagree with itself.
     *
     * Distinct from the eight `FORM_OUTPUT_*` types, which are machine-rendered
     * outputs of a form INSTANCE. This is human-supplied material owned by the
     * package being assembled, and it reads under the same grant the package
     * itself reads under (`forms:instance:read`).
     *
     * Registered in ALL THREE places a new owner type must appear: this union,
     * `OWNER_TYPE_DOWNLOAD_POLICY` and `GenericAttachmentService`'s
     * `OWNER_TYPE_ALLOWLIST` (plus `OWNER_TYPE_COLLECTIONS`, which the compiler
     * forces because it is a total `Record`).
     */
    SUBMISSION_PACKAGE_DOCUMENT: 'submission_package_document',
    // Sprint 41.6.0 V2.6 — Vendor-risk file-based artifacts
    VENDOR_CONTRACT: 'vendor_contract',
    VENDOR_SOC_REPORT: 'vendor_soc_report',
    VENDOR_DPA: 'vendor_dpa',
    VENDOR_NDA: 'vendor_nda',
    VENDOR_TERMINATION_NOTICE: 'vendor_termination_notice',
    VENDOR_AMENDMENT: 'vendor_amendment',
    // Sprint 43.1.0 batch 2 — Rights Request artefacts (E3 Case File PDF,
    // E5 Phụ lục VI Evidence Pack ZIP). Both are standalone, org-owned
    // exports persisted via UnifiedExportService → grc_attachments. The
    // CASE_EVIDENCE_PACK_EXPORT entry above is the legacy stub owner type
    // produced by the now-renamed CaseEvidencePackService; the two new
    // entries below replace it for the comprehensive 13-section PDF and
    // the assembled ZIP. The legacy entry is retained for backwards
    // compatibility with any historical rows already persisted under it.
    RIGHTS_REQUEST_CASE_FILE: 'rights_request_case_file',
    RIGHTS_REQUEST_EVIDENCE_PACK: 'rights_request_evidence_pack',
    // Sprint 43.2.0 batch 2 (E3) — Bulk case export. Standalone org-owned
    // export persisted via UnifiedExportService → grc_attachments. Covers
    // the pdf (aggregated summary), csv (delegated to exportCasesCSV),
    // and zip ("ZIP of ZIPs" of per-case evidence packs + index.json)
    // produced by POST /rights-requests/cases/export/bulk.
    RIGHTS_REQUEST_BULK_EXPORT: 'rights_request_bulk_export',
    // Sprint 43.5.5 — Vendor Risk register exports migrated to
    // UnifiedExportService so they appear in the Files Browser and
    // support multi-format output (CSV/XLSX/JSON).
    VENDOR_RISK_ACCEPTANCE_EXPORT: 'vendor_risk_acceptance_export',
    VENDOR_SUBPROCESSOR_REGISTER_EXPORT: 'vendor_subprocessor_register_export',
    // Sprint 43.6.0 W4 — Vendor Risk Questionnaire exports (R-33).
    // Filled instance exports (PDF/DOCX audit record, CSV/XLSX analytics)
    // and the portfolio-list export (CSV/XLSX) flow through
    // UnifiedExportService the same way risk-acceptance-register exports
    // do. Standalone org-owned; owner_id = null. Template-level exports
    // (blank questionnaire as DOCX for vendor send-out) use the separate
    // VENDOR_QUESTIONNAIRE_TEMPLATE_EXPORT owner type so Files Browser
    // filtering can distinguish filled instances from blank templates.
    VENDOR_QUESTIONNAIRE_EXPORT: 'vendor_questionnaire_export',
    VENDOR_QUESTIONNAIRE_TEMPLATE_EXPORT: 'vendor_questionnaire_template_export',
    // Sprint 43.7.0 Wave 5 — Governance Model per-model exports (CSV /
    // XLSX / JSON / PDF / DOCX) through UnifiedExportService. Standalone
    // org-owned; owner_id references the `_id` of the governance model
    // row when supplied by the route.
    GOVERNANCE_MODEL_EXPORT: 'governance_model_export',
    // Sprint 43.7.2 Wave 3 — R-33 migration for 7 Program Governance
    // list/dashboard pages that previously used `downloadClientExport()`.
    // All flow through UnifiedExportService.generateFromDefinition now
    // (csv/xlsx/pdf/docx) and persist as standalone org-owned exports
    // in the File Component. AuditReadiness exports the latest score +
    // history snapshot; the other six export their filtered list.
    AUDIT_READINESS_EXPORT: 'audit_readiness_export',
    DOCUMENT_CHANGE_LOG_EXPORT: 'document_change_log_export',
    GOVERNANCE_DOCUMENTS_EXPORT: 'governance_documents_export',
    DOCUMENT_ISSUANCE_EXPORT: 'document_issuance_export',
    MATURITY_MODELS_EXPORT: 'maturity_models_export',
    // Sprint 48.2.5 — Forseti maturity walkthrough DOCX deliverable
    // (improvement-roadmap report drawn from a persisted walkthrough draft).
    // Standalone org-owned (owner_id = null); orphan detection N/A. Distinct
    // from `maturity_models_export` (the per-page list export) so the Files
    // Browser can filter Forseti-generated walkthrough roadmaps from generic
    // maturity-model list exports.
    MATURITY_WALKTHROUGH_EXPORT: 'maturity_walkthrough_export',
    // Sprint 48.2.9 — Forseti audit walkthrough DOCX deliverable
    // (per-control walkthrough script drawn from a persisted walkthrough
    // draft). Auditor uses the deliverable as the field-prep document
    // for an audit kickoff.
    AUDIT_WALKTHROUGH_EXPORT: 'audit_walkthrough_export',
    REMEDIATION_PLANS_EXPORT: 'remediation_plans_export',
    REPORTING_PERIODS_EXPORT: 'reporting_periods_export',
    TREND_METRICS_EXPORT: 'trend_metrics_export',
    // Sprint 43.7.2 Wave 3 residual closeout — the initial wave-3 audit
    // missed 4 program-governance pages still using
    // `downloadClientExport()` or `window.print()`. These route their
    // exports through UnifiedExportService like the rest:
    //   ACKNOWLEDGMENT_EXPORT — AcknowledgmentTrackingPage list.
    //   DOC_DRAFTING_EXPORT — DocumentDraftingPage (governance_documents
    //     filtered to draft/in_review/approved statuses).
    //   PROGRAM_HEALTH_EXPORT — ProgramHealthPage KPI snapshot + per-
    //     module trend scorecards aggregated in one report.
    //   RACI_MATRIX_EXPORT — ObligationMappingsPage RACI matrix view
    //     (replaces `window.print()` print dialog).
    // Standalone org-owned; owner_id = null for all four.
    ACKNOWLEDGMENT_EXPORT: 'acknowledgment_export',
    DOC_DRAFTING_EXPORT: 'doc_drafting_export',
    PROGRAM_HEALTH_EXPORT: 'program_health_export',
    RACI_MATRIX_EXPORT: 'raci_matrix_export',
    // Sprint 43.7.3 Wave 3 — R-33 migration for 9 Monitoring list/dashboard
    // pages that previously used `downloadClientExport()`. All route through
    // UnifiedExportService.generateFromDefinition (csv/xlsx/pdf/docx) and
    // persist as standalone org-owned exports in the File Component. The
    // 10th Monitoring page (GovernanceRaciPage) reuses the existing
    // RACI_MATRIX_EXPORT owner type via `/program-governance/raci/export`.
    MONITORING_AUTHORITY_READINESS_EXPORT: 'monitoring_authority_readiness_export',
    MONITORING_CHANGE_REVIEWS_EXPORT: 'monitoring_change_reviews_export',
    MONITORING_FRAMEWORK_LINKS_EXPORT: 'monitoring_framework_links_export',
    MONITORING_KPI_EXPORT: 'monitoring_kpi_export',
    MONITORING_PERIODS_EXPORT: 'monitoring_periods_export',
    MONITORING_PROGRAMS_EXPORT: 'monitoring_programs_export',
    MONITORING_RIC_CASES_EXPORT: 'monitoring_ric_cases_export',
    MONITORING_TASK_TEMPLATES_EXPORT: 'monitoring_task_templates_export',
    MONITORING_TASKS_EXPORT: 'monitoring_tasks_export',
    // Sprint 44.0.1 Wave 3 — R-33 migration for the 3 Consent Properties
    // pages that were still using `downloadClientExport()` (Binding,
    // Registry; ChangeRequestPipeline surfaced via grep scope expansion).
    // All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx) and persist as standalone org-owned exports
    // in the File Component. owner_id = null.
    CONSENT_PROPERTY_BINDING_EXPORT: 'consent_property_binding_export',
    CONSENT_PROPERTY_REGISTRY_EXPORT: 'consent_property_registry_export',
    CONSENT_PROPERTY_CHANGE_REQUESTS_EXPORT: 'consent_property_change_requests_export',
    // Sprint 44.0.2 Wave 3 — R-33 migration for 8 Data Discovery list/dashboard
    // pages. All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx). The 4 other Discovery pages (Dashboard, Cycles,
    // OwnerReview, Lineage) reuse the existing DISCOVERY_REPORT_EXPORT via
    // `/discovery/export/*` from Sprint 42.2.3 E2.
    DISCOVERY_CLASSIFICATION_RULES_EXPORT: 'discovery_classification_rules_export',
    DISCOVERY_DEDUP_RULES_EXPORT: 'discovery_dedup_rules_export',
    DISCOVERY_IMPORT_TEMPLATES_EXPORT: 'discovery_import_templates_export',
    DISCOVERY_CONNECTORS_EXPORT: 'discovery_connectors_export',
    DISCOVERY_SCANS_EXPORT: 'discovery_scans_export',
    DISCOVERY_STAGING_EXPORT: 'discovery_staging_export',
    DISCOVERY_SUGGESTIONS_EXPORT: 'discovery_suggestions_export',
    DISCOVERY_ANALYTICS_EXPORT: 'discovery_analytics_export',
    // Sprint 44.0.3 Waves 2+3 — R-33 migration for all 11 Connectors list
    // pages. Prior to this sprint the `/connectors/export/:artifactType`
    // route returned raw JSON (never a file) and all 11 pages discarded
    // the response. Now each artifact type renders through
    // UnifiedExportService.generateFromDefinition (csv/xlsx/pdf/docx) and
    // persists as a standalone org-owned File Component export
    // (owner_id = null).
    CONNECTOR_AUTOMATION_PROGRAMS_EXPORT: 'connector_automation_programs_export',
    CONNECTOR_SYSTEM_INVENTORY_EXPORT: 'connector_system_inventory_export',
    CONNECTOR_TRACEABILITY_MATRIX_EXPORT: 'connector_traceability_matrix_export',
    CONNECTOR_GAP_REGISTER_EXPORT: 'connector_gap_register_export',
    CONNECTOR_BRDS_EXPORT: 'connector_brds_export',
    CONNECTOR_DECISION_RECORDS_EXPORT: 'connector_decision_records_export',
    CONNECTOR_CONTROL_MAPPINGS_EXPORT: 'connector_control_mappings_export',
    CONNECTOR_INSTANCES_EXPORT: 'connector_instances_export',
    CONNECTOR_DEFINITIONS_EXPORT: 'connector_definitions_export',
    CONNECTOR_EVENT_BUS_EXPORT: 'connector_event_bus_export',
    CONNECTOR_POC_SCORECARDS_EXPORT: 'connector_poc_scorecards_export',
    CONNECTOR_SECURITY_REVIEWS_EXPORT: 'connector_security_reviews_export',
    CONNECTOR_CREDENTIALS_EXPORT: 'connector_credentials_export',
    // Sprint 44.0.4 — Deletion Orchestration QA sweep
    // Backend-rendered exports replacing client-side `downloadClientExport`
    // violations on ExecutionTasksPage, VendorDeletionRequestsPage and
    // ProofChainPage. All flow through UnifiedExportService.
    DELETION_EXECUTION_TASKS_EXPORT: 'deletion_execution_tasks_export',
    DELETION_VENDOR_REQUESTS_EXPORT: 'deletion_vendor_requests_export',
    DELETION_PROOF_CHAIN_EXPORT: 'deletion_proof_chain_export',
    // Sprint 44.0.5 Wave 3 — R-33 migration for 6 Incident Operations list
    // pages. All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx) and persist as standalone org-owned exports in
    // the File Component (owner_id = null). The SLA dashboard + response
    // metrics exports share a backend service method but carry distinct
    // AttachmentOwnerType values so the File Component audit trail
    // distinguishes which page emitted each artefact.
    INCIDENT_OPS_PLAYBOOK_TEMPLATES_EXPORT: 'incident_ops_playbook_templates_export',
    INCIDENT_OPS_REGULATOR_EXPORTS_EXPORT: 'incident_ops_regulator_exports_export',
    INCIDENT_OPS_RESPONSE_METRICS_EXPORT: 'incident_ops_response_metrics_export',
    INCIDENT_OPS_SLA_DASHBOARD_EXPORT: 'incident_ops_sla_dashboard_export',
    INCIDENT_OPS_SLA_DEFINITIONS_EXPORT: 'incident_ops_sla_definitions_export',
    INCIDENT_OPS_VENDOR_COORDINATION_EXPORT: 'incident_ops_vendor_coordination_export',
    // Sprint 44.0.6 Wave 2 — R-33 migration for Access Accountability
    // list/dashboard + entity-detail exports. Upgrades 8 pages that used
    // the 1-format `ACCESS_ACCOUNTABILITY_EXPORT` pass-through to 4-format
    // `generateFromDefinition`, and replaces the `new Blob([JSON...])`
    // direct client-side JSON export on the Evidence Package detail page
    // with a backend multi-format export. Standalone org-owned
    // (owner_id = null); orphan detection not applicable.
    ACCESS_SESSION_EVENTS_EXPORT: 'access_session_events_export',
    ACCESS_ANCHORING_PROOFS_EXPORT: 'access_anchoring_proofs_export',
    ACCESS_JUSTIFICATIONS_EXPORT: 'access_justifications_export',
    ACCESS_INVESTIGATIONS_LIST_EXPORT: 'access_investigations_list_export',
    ACCESS_INVESTIGATION_CASE_EXPORT: 'access_investigation_case_export',
    ACCESS_LOG_LIST_EXPORT: 'access_log_list_export',
    ACCESS_POLICIES_EXPORT: 'access_policies_export',
    ACCESS_REVIEW_CAMPAIGNS_EXPORT: 'access_review_campaigns_export',
    ACCESS_EVIDENCE_PACKAGES_EXPORT: 'access_evidence_packages_export',
    ACCESS_EVIDENCE_PACKAGE_DETAIL_EXPORT: 'access_evidence_package_detail_export',
    // Sprint 44.0.7 Wave 2 — R-33 migration for Organizations + Org Groups
    // list/dashboard pages. All standalone org-owned (owner_id = null);
    // orphan detection N/A. The pre-existing ORG_GROUP_AUDIT_EXPORT from
    // Sprint 42.2.3 E2 remains for the legacy `POST /audit/export` path
    // used programmatically; the new consolidated + per-org list exports
    // below have their own owner types so Files Browser can filter them.
    ORGANIZATIONS_EXPORT: 'organizations_export',
    ORGANIZATION_INTAKE_CONFIGS_EXPORT: 'organization_intake_configs_export',
    ORGANIZATION_DEPARTMENTS_EXPORT: 'organization_departments_export',
    ORGANIZATION_FRAMEWORK_ASSIGNMENTS_EXPORT: 'organization_framework_assignments_export',
    ORGANIZATION_COMPLIANCE_ROLLUP_EXPORT: 'organization_compliance_rollup_export',
    ORG_GROUPS_LIST_EXPORT: 'org_groups_list_export',
    ORG_GROUP_DASHBOARD_EXPORT: 'org_group_dashboard_export',
    ORG_GROUP_REPORTS_EXPORT: 'org_group_reports_export',
    ORG_GROUP_CONSOLIDATED_AUDIT_EXPORT: 'org_group_consolidated_audit_export',
    ORG_GROUP_PER_ORG_AUDIT_EXPORT: 'org_group_per_org_audit_export',
    ORG_GROUP_USERS_EXPORT: 'org_group_users_export',
    ORG_GROUP_SHARED_VENDORS_EXPORT: 'org_group_shared_vendors_export',
    ORG_GROUP_SHARED_FRAMEWORKS_EXPORT: 'org_group_shared_frameworks_export',
    ORG_GROUP_SHARED_FORM_TEMPLATES_EXPORT: 'org_group_shared_form_templates_export',
    ORG_GROUP_MERGED_VENDORS_EXPORT: 'org_group_merged_vendors_export',
    // Sprint 44.0.8 Wave 3 — R-33 migration for 14 Partner Workspace pages.
    // All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx). Standalone org-owned (owner_id = null). The
    // pre-existing `POST /audit/export` path (Sprint 42.0.0 F1) remains for
    // programmatic consumers; the new `GET /audit/export-list` drives the UI.
    PARTNER_ORGS_EXPORT: 'partner_orgs_export',
    PARTNER_ORG_DETAIL_EXPORT: 'partner_org_detail_export',
    PARTNER_DASHBOARD_EXPORT: 'partner_dashboard_export',
    PARTNER_REPORTS_EXPORT: 'partner_reports_export',
    PARTNER_AUDIT_LIST_EXPORT: 'partner_audit_list_export',
    PARTNER_WORKFLOW_TEMPLATES_EXPORT: 'partner_workflow_templates_export',
    PARTNER_WORKFLOW_INSTANCES_EXPORT: 'partner_workflow_instances_export',
    PARTNER_DELIVERABLES_EXPORT: 'partner_deliverables_export',
    PARTNER_DELIVERABLE_TEMPLATES_EXPORT: 'partner_deliverable_templates_export',
    PARTNER_SUPPORT_TICKETS_EXPORT: 'partner_support_tickets_export',
    PARTNER_TICKET_DETAIL_EXPORT: 'partner_ticket_detail_export',
    // Operator-uploaded evidence on a support ticket. Unlike the *_EXPORT owner
    // types above (which are generated artefacts), this one carries files a human
    // attaches to a ticket. `grc_partner_support_tickets.attachments[]` stores the
    // returned `file_id`; the bytes live in the generic attachment store.
    PARTNER_TICKET_ATTACHMENT: 'partner_ticket_attachment',
    PARTNER_ESCALATION_DASHBOARD_EXPORT: 'partner_escalation_dashboard_export',
    PARTNER_SERVICE_TIERS_EXPORT: 'partner_service_tiers_export',
    PARTNER_BRAND_PROFILES_EXPORT: 'partner_brand_profiles_export',
    // Sprint 44.0.9 Wave 3 — R-33 migration for 12 Task Management pages.
    // All route through UnifiedExportService.generateFromDefinition
    // (csv/xlsx/pdf/docx). Standalone org-owned (owner_id = null). The
    // pre-existing `GET /tasks/export` (Sprint 17.0.0, gated by TM_EXPORT)
    // remains for programmatic consumers; the new `GET /tasks/export-list`
    // drives the TaskListPage UI. Sprint 44.0.9 CLOSES the 44.0.x series.
    TASK_LIST_EXPORT: 'task_list_export',
    TASK_BOARD_EXPORT: 'task_board_export',
    TASK_DETAIL_EXPORT: 'task_detail_export',
    MY_TASKS_EXPORT: 'my_tasks_export',
    OVERDUE_TASKS_EXPORT: 'overdue_tasks_export',
    TASK_CALENDAR_EXPORT: 'task_calendar_export',
    WORKFLOW_LIST_EXPORT: 'workflow_list_export',
    WORKFLOW_DETAIL_EXPORT: 'workflow_detail_export',
    WORKFLOW_TEMPLATE_CATALOG_EXPORT: 'workflow_template_catalog_export',
    TASK_CONFLICT_DETECTION_EXPORT: 'task_conflict_detection_export',
    TASK_CONSOLIDATION_EXPORT: 'task_consolidation_export',
    TASK_SECTION_ASSIGNMENTS_EXPORT: 'task_section_assignments_export',
    // Fix Sprint 20260418-0500 Finding 14 — R-33 migration for FormOutputsPage
    // which previously triggered `window.print()` (a forbidden client-side
    // export pattern). The new `GET /forms/instances/:instanceId/print-export`
    // route renders the form instance through UnifiedExportService.
    // generateFromDefinition (PDF only) and persists as a standalone
    // org-owned export in the File Component (owner_id = null).
    FORM_OUTPUT_PRINT_EXPORT: 'form_output_print_export',
    // Follow-up 2 — Cover-letter auto-generation (2026-04-21). Standalone org-
    // owned exports persisted via UnifiedExportService → grc_attachments.
    // Replaces the two form-schema-based manual cover letters
    // (vn_pdpl2025_dpia_cover_letter + vn_mps_submission_cover_letter).
    COVER_LETTER_DPIA_DOSSIER: 'cover_letter_dpia_dossier',
    COVER_LETTER_MPS_SUBMISSION: 'cover_letter_mps_submission',
    // Sprint 58.1.0 (Phase 25 / Sprint B) — Manual sent-proof upload for
    // the PDPL evidence-pack email flow. Operators upload a screenshot or
    // raw .eml after sending the email through their own mail client (the
    // architecture rule LOCKS direct-send to customer-controlled
    // infrastructure — AesirX never sends on the customer's behalf).
    // Owner_id is the `pack_id`, so each proof links 1:N to the assembly
    // doc. Immutable once attached: no DELETE / PATCH on the
    // grc_pdpl_email_sent_proofs collection.
    PDPL_EMAIL_SEND_PROOF_2026: 'pdpl_email_send_proof_2026',
    // Sprint 58.3.0 (Phase 25 / Sprint D) — Direct-send chain-of-custody
    // artefacts. Owner_id is the send_id from grc_pdpl_email_send_records.
    // PDPL_AUTHORITY_RESPONSE_EVIDENCE_2026 carries the authority's reply
    // (`.eml`) or scan; PDPL_HARD_COPY_RECEIPT_2026 carries postal receipt
    // evidence (courier slip, signed acknowledgement). Both append-only
    // from the service's perspective — once attached, they are never
    // edited (a re-upload creates a NEW attachment row).
    PDPL_AUTHORITY_RESPONSE_EVIDENCE_2026: 'pdpl_authority_response_evidence_2026',
    PDPL_HARD_COPY_RECEIPT_2026: 'pdpl_hard_copy_receipt_2026',
    // Sprint 62.6.0 — the regulatory artifact registry's export.
    //
    // `GET /regulatory-artifacts/export` selects rows through the same access
    // filter the list uses, so a restricted artifact the caller holds no grant
    // for is absent from the export as it is from every other read. This owner
    // type is what lets that selection become a *file* through
    // `UnifiedExportService` (R-33) rather than only a JSON envelope.
    //
    // Registered in both structures deliberately: Sprint 62.2.2 shipped a new
    // owner type in one of the two that must agree and a pre-existing drift test
    // caught it. The two are `AttachmentOwnerType` here and `OWNER_TYPE_ALLOWLIST`
    // in `attachments/generic-service.ts`; a value in one and not the other is a
    // route accepting an `owner_type` the service rejects — a silent 422.
    REGULATORY_ARTIFACTS_EXPORT: 'regulatory_artifacts_export',
    // Sprint 62.7.0 — the three evidence domains.
    //
    // THREE structures must agree, not two. `OWNER_TYPE_COLLECTIONS` in
    // `attachments/generic-service.ts` is the third, and only the other two are
    // guarded by `allowlist-drift.test.ts`; the collections map is guarded solely
    // by `Record` exhaustiveness, which is a compile error rather than a silent
    // 422 and is the reason it has never drifted.
    //
    // Six operator-uploaded types and three exports. The operator-uploaded ones
    // MUST be in the allowlist or the field can never be filled — 62.2.5 shipped
    // a mandatory `hard_copy.proof` with no owner type, so nothing in the product
    // could satisfy a field the server required.
    PUBLIC_DATA_SHARING_AGREEMENT: 'public_data_sharing_agreement',
    PUBLIC_DATA_ACKNOWLEDGEMENT: 'public_data_acknowledgement',
    EKYC_DISPOSAL_EVIDENCE: 'ekyc_disposal_evidence',
    PAYMENT_DATA_SECURITY_EVIDENCE: 'payment_data_security_evidence',
    PLATFORM_SELLER_REGISTRATION_EVIDENCE: 'platform_seller_registration_evidence',
    PLATFORM_DOCUMENT_EVIDENCE: 'platform_document_evidence',
    PUBLIC_DATA_EXPORT: 'public_data_export',
    PAYMENTS_EVIDENCE_EXPORT: 'payments_evidence_export',
    PLATFORM_EVIDENCE_EXPORT: 'platform_evidence_export',
    /**
     * Sprint 62.46.0 — the AML programme inspection pack (scoping record,
     * configuration history and filing register for a date range; case
     * narratives only when asked for under `aml_case:approve`). Owns no row.
     * Retrieval follows its creation gate, `aml_case:read`, for the reason the
     * case evidence type states: the pack names cases.
     */
    AML_PROGRAMME_EXPORT: 'aml_programme_export',
    // Sprint 62.8.0 — manufacturing operations.
    //
    // **Five stored types declared an attachment array and no owner type existed for
    // any of them**, so all five fields were unfillable: a conformity record could
    // claim to hold a test certificate against a string nothing in the product could
    // produce. That is 62.2.5's `hard_copy.proof` defect — mandatory and
    // unsatisfiable — reproduced five times over, and 64.0.0's fail-closed download
    // policy means even a hypothetical upload would have been T1-download-only.
    //
    // Found at gate E12.5 by asking why nine other reference classes in the same
    // module are validated by collection lookup and these were not.
    //
    // **FOUR structures must agree**, and the count has grown twice: this type,
    // `OWNER_TYPE_ALLOWLIST` and `OWNER_TYPE_COLLECTIONS` in
    // `attachments/generic-service.ts`, and `OWNER_TYPE_DOWNLOAD_POLICY` in
    // `constants/attachment-download-policy.ts`. Only the first two are guarded by
    // `allowlist-drift.test.ts`; the collections map is held by `Record`
    // exhaustiveness (a compile error, which is why it has never drifted) and the
    // download policy fails closed to a T1-only grant for an unmapped type, which is
    // safe and silent — a file nobody can retrieve rather than a 422.
    MANUFACTURING_TRACE_EVIDENCE: 'manufacturing_trace_evidence',
    MANUFACTURING_CONFORMITY_EVIDENCE: 'manufacturing_conformity_evidence',
    MANUFACTURING_EPR_FULFILMENT_EVIDENCE: 'manufacturing_epr_fulfilment_evidence',
    /**
     * Proof that a producer-responsibility filing was made, and the authority's
     * response.
     *
     * Its own type rather than sharing the fulfilment one: a filing proof is a
     * document an authority issued or received, and 64.0.0's download policy gates
     * retrieval per owner type — collapsing the two would give whoever may read
     * recycling evidence the right to read correspondence with a regulator.
     */
    MANUFACTURING_EPR_SUBMISSION_PROOF: 'manufacturing_epr_submission_proof',
    MANUFACTURING_COMPOSITION_EVIDENCE: 'manufacturing_composition_evidence',
    /**
     * Session logs, screen recordings and ticket references for a remote-access
     * grant.
     *
     * The most sensitive of the six: a recording of a vendor session inside plant
     * machinery. Its download gate follows the module's read permission rather than
     * the broader file-detail grant, for the reason 64.0.0 established — retrieval
     * must honour the permission that gated creation.
     */
    MANUFACTURING_REMOTE_ACCESS_EVIDENCE: 'manufacturing_remote_access_evidence',
    MANUFACTURING_EXPORT: 'manufacturing_export',
    // Sprint 62.9.0 — shipment, customs, clearance, delivery, claims,
    // authorisations. Registered in all three structures named above.
    LOGISTICS_SHIPMENT_DOCUMENT: 'logistics_shipment_document',
    LOGISTICS_CUSTOMS_EVIDENCE: 'logistics_customs_evidence',
    LOGISTICS_CLEARANCE_EVIDENCE: 'logistics_clearance_evidence',
    /**
     * A signature image, a doorstep photograph, a delivery scan.
     *
     * **The most sensitive owner type in this module, and the reason it is its own
     * rather than sharing the parcel one.** This is material about an identified
     * person at an identified address at a known time. Its download gate is
     * `logistics_delivery_proof:read` — a permission that exists precisely so that
     * seeing where a parcel is and seeing a photograph of who took it are
     * separable grants. Collapsing it onto `logistics_parcel:read` would hand
     * every dispatcher the register the courier persona says nobody owns.
     */
    LOGISTICS_DELIVERY_PROOF: 'logistics_delivery_proof',
    LOGISTICS_CLAIM_EVIDENCE: 'logistics_claim_evidence',
    LOGISTICS_AUTHORISATION_EVIDENCE: 'logistics_authorisation_evidence',
    /**
     * Evidence hung on one event of a parcel's timeline — a scan, a depot
     * photograph, a handover note.
     *
     * `ParcelEvent.evidence_attachment_ids` shipped with **no owner type at all**,
     * so the field existed and nothing in the product could fill it: 62.2.5's
     * `hard_copy.proof` defect, repeated. Gate E12.5 pass 2.
     *
     * Its download gate is `logistics_parcel:read`, **not** something narrower.
     * 64.0.0's settled principle is that retrieval honours the grant that gated
     * creation, and a parcel event is created and read under the parcel grant; a
     * narrower gate reproduces the mismatch inverted, with the bytes stricter than
     * the row they hang on. The "it might be a photograph" argument does not
     * distinguish this attachment from `detail` and `location`, which the same
     * grant already returns. `LOGISTICS_DELIVERY_PROOF` stays separate because its
     * whole register is separate — a signature belonging to a named person at a
     * known address — and that separation is a permission, not a guess about
     * content.
     */
    LOGISTICS_PARCEL_EVENT_EVIDENCE: 'logistics_parcel_event_evidence',
    /**
     * An export of the **shipment-side** registers — shipments and cargo lines.
     *
     * Named `logistics_export` rather than `logistics_shipment_export` because
     * stored rows carry the string: renaming it would drop every artefact ever
     * produced by this module into `OWNER_TYPE_DOWNLOAD_FALLBACK`, retrievable by
     * T1 alone. The narrowing is done by moving the other registers **off** this
     * type, not by renaming it.
     */
    LOGISTICS_EXPORT: 'logistics_export',
    /**
     * An export of the customs-side registers — customs records, the clearance
     * ledger and authority-correspondence links.
     *
     * One owner type per **grant**, not per register: all three are created under
     * `logistics_customs:read`, so one type expresses the gate exactly and three
     * would be three copies of one fact.
     */
    LOGISTICS_CUSTOMS_EXPORT: 'logistics_customs_export',
    /** An export of the parcel register or a parcel timeline. `logistics_parcel:read`. */
    LOGISTICS_PARCEL_EXPORT: 'logistics_parcel_export',
    /** An export of the parcel-claims register. `logistics_claim:read`. */
    LOGISTICS_CLAIM_EXPORT: 'logistics_claim_export',
    /** An export of the operating-authorisations register. `logistics_authorisation:read`. */
    LOGISTICS_AUTHORISATION_EXPORT: 'logistics_authorisation_export',
    /**
     * An export of the delivery-proof register, separated from `LOGISTICS_EXPORT`.
     *
     * **This exists because the first version of this module undid its own
     * separation at the download step.** Creation is gated per register, retrieval
     * per *owner type* — so a single `logistics_export` type mapped to
     * `logistics_shipment:read` made every export retrievable by the broadest
     * reader in the module, including the one register whose whole point is that
     * it is not: signature images and doorstep photographs of identified people.
     *
     * A control that is separated at one step and collapsed at the next is not
     * separated. Found by the stream building the export service, not by any gate.
     */
    LOGISTICS_DELIVERY_PROOF_EXPORT: 'logistics_delivery_proof_export',
    /**
     * Sprint 62.53.0 (AC4, #665) — evidence behind an organisation-profile
     * statement. `owner_id` is the ATTRIBUTE name (`data_exploitation_routes`,
     * `regulated_entity_types`), not a statement row: a file is uploaded before
     * the revision that names it exists, and every revision of the attribute may
     * name it. Org-owned (`OWNER_TYPE_COLLECTIONS` null). A file any revision
     * names cannot be deleted (`ATTACHMENT_EVIDENCE_DEPENDENTS`).
     */
    ORG_PROFILE_STATEMENT: 'org_profile_statement',
    PUBLICATION_PREREQUISITE_EVIDENCE: 'publication_prerequisite_evidence',
    /**
     * Sprint 62.57.0 (Slice R, AC-R1, work file D3) — the copy of an authority's
     * written communication, as the organisation holds it. Owned by the
     * COMMUNICATION (`owner_id` is its `communication_id`, which is also its
     * `_id`), uploaded after the entry is recorded; the entry is never refused
     * for having none (it reads *Not evidenced*). The owner resolves only within
     * the uploading organisation (`OWNER_RECORD_WRITE_GRANTS`), so an id of
     * another organisation's communication is the same 404 as an unknown one.
     * Downloads under the filing read; uploading or removing one needs the
     * filing write grant (`dpia:write`, derived). The entry stores no file id,
     * so no row rests on the file (`ATTACHMENT_EVIDENCE_DEPENDENTS`: none).
     */
    DOSSIER_AUTHORITY_COMMUNICATION: 'dossier_authority_communication',
    /**
     * Sprint 62.57.0 (Slice R, AC-R5, work file D5) — one filing's communication
     * history exported through `UnifiedExportService` (R-33). Written only by
     * `DossierAuthorityCommunicationService.export`; `owner_id` is the filing's
     * `filing_id`. Downloads under the filing read that produces it.
     */
    DOSSIER_AUTHORITY_COMMUNICATION_EXPORT: 'dossier_authority_communication_export',
    /**
     * Sprint 62.58.0 (Slice C, AC-C3) — a document behind a briefing or a
     * consultation comment recorded on a framework (the slides, the letter sent,
     * the summary export the export dialog offers to attach). Owned by the ENTRY
     * (`owner_id` is its `briefing_id`, which is also its `_id`), uploaded after
     * the entry is recorded; the entry is never refused for having none. The
     * owner resolves only within the uploading organisation
     * (`OWNER_RECORD_WRITE_GRANTS`), so another organisation's entry id is the
     * same 404 as an unknown one. Downloads under `regulatory_framework:read`;
     * uploading or removing one needs `regulatory_framework:write` (derived).
     * The entry stores no file id, so no row rests on the file
     * (`ATTACHMENT_EVIDENCE_DEPENDENTS`: none).
     */
    FRAMEWORK_BRIEFING: 'framework_briefing',
    /**
     * Sprint 62.58.0 (Slice C, AC-C1) — a framework summary exported through
     * `UnifiedExportService` (R-33). Written only by
     * `FrameworkSummaryExportService.export`; `owner_id` is the framework's
     * `framework_id`. Downloads under `regulatory_framework:read`, the read that
     * produces it.
     */
    FRAMEWORK_SUMMARY_EXPORT: 'framework_summary_export',
    /**
     * Sprint 62.57.0 Slice E (work file D60) — a document held on a
     * processing-service certificate (the certificate in paper or electronic
     * form, the issue or revocation decision, the dossier as filed), typed by
     * kind on the record. `owner_id` is the certificate's id. Downloads under
     * `service_certificate:read`; uploading needs `service_certificate:write`.
     * Protected while the certificate names it.
     */
    SERVICE_CERTIFICATE_DOCUMENT: 'service_certificate_document',
    /**
     * Sprint 62.57.0 Slice E (D59) — a file behind one dossier part of a
     * certificate application. `owner_id` is `{application}:{part}`. Downloads
     * under `service_certificate:read`; uploading needs `service_certificate:write`.
     */
    SERVICE_CERTIFICATE_APPLICATION_DOCUMENT: 'service_certificate_application_document',
    /**
     * Sprint 62.57.0 Slice E (D57) — the copy of an authority's communication on
     * a certificate or an application, as the organisation holds it: the Slice R
     * rule (`owner_id` is the communication's id; the entry is never refused for
     * having none, and may lose its file while the entry stays). Downloads under
     * `service_certificate:read`.
     */
    SERVICE_CERTIFICATE_COMMUNICATION: 'service_certificate_communication',
    /**
     * Sprint 62.57.0 Slice E (D66) — the register or one application exported
     * through `UnifiedExportService` (R-33). Written by the platform only.
     * Downloads under `service_certificate:read`.
     */
    SERVICE_CERTIFICATE_EXPORT: 'service_certificate_export',
    /**
     * Sprint 62.57.0 Slice E (stream K2, coordinator ruling (e), D61) — a file of
     * the organisation's return of a revoked certificate. `owner_id` is the
     * certificate's id; protected while a return names it. Downloads under
     * `service_certificate:read`; uploading needs `service_certificate:write`.
     */
    SERVICE_CERTIFICATE_RETURN: 'service_certificate_return',
    /**
     * Sprint 62.57.0 Slice E (stream K2, ruling (e), D56) — a file of a
     * supplement the organisation sent in answer to a deficiency notice.
     * `owner_id` is the application's id; protected while a supplement names it.
     * Downloads under `service_certificate:read`.
     */
    SERVICE_CERTIFICATE_SUPPLEMENT_SENT: 'service_certificate_supplement_sent',
    /**
     * Sprint 62.57.0 Slice E (§12.13, D79, D81) — evidence of what a vendor stated
     * or showed about its processing-service certificate, or of the services it
     * provides on the organisation's behalf. `owner_id` is the vendor's id.
     * Downloads under `gov:vendor:read`; uploading needs `gov:vendor:write`.
     * Never a verification.
     */
    VENDOR_SERVICE_CERTIFICATE_EVIDENCE: 'vendor_service_certificate_evidence',
    /**
     * Sprint 62.58.0 (Slice N, AC-N3) — a document behind one revision of a
     * processing activity's deployment access statement (the access policy, the
     * extraction software's role list, the signed approval). Owned by the
     * REVISION (`owner_id` is its `statement_id`, which is also its `_id`),
     * uploaded after the revision is recorded; a revision is never refused for
     * having none. The owner resolves only within the uploading organisation
     * (`OWNER_RECORD_WRITE_GRANTS`), so another organisation's revision id is the
     * same 404 as an unknown one. Downloads under `dm:activity:read`; uploading
     * or removing one needs `dm:activity:write` (derived). The revision stores
     * no file id, so no row rests on the file (`ATTACHMENT_EVIDENCE_DEPENDENTS`:
     * none).
     */
    ACCESS_STATEMENT: 'access_statement',
};
// ── Sprint 63.31.0 (#410, Q4, OD-C, E10) — release of a depended-on file ──
/** The collection a release-at-retention record is written to. */
export const ATTACHMENT_RETENTION_RELEASE_COLLECTION = 'grc_attachment_retention_releases';
/**
 * Why a depended-on file was released: its retention period ended
 * (`retention_ended`), or a rights request for erasure reached it (`erasure`).
 */
export const ATTACHMENT_RELEASE_BASES = ['retention_ended', 'erasure'];
//# sourceMappingURL=attachment.js.map